import { useMemo, useState } from "react";
import { useEditorStore } from "@/store";
import type { StoreLayer } from "@/store";
import { useNetworkStats } from "@/hooks/useNetworkStats";
import type { MapRef } from "react-map-gl/maplibre";

interface MonitorPanelProps {
  mapRef: React.RefObject<MapRef | null>;
}

export function MonitorPanel({ mapRef }: MonitorPanelProps) {
  const style = useEditorStore((s) => s.style);
  const viewport = useEditorStore((s) => s.viewport);
  const layers = style.layers;
  const sources = (style.sources ?? {}) as Record<string, Record<string, unknown>>;

  const stats = useMemo(() => computeStats(layers, sources, viewport.zoom), [layers, sources, viewport.zoom]);
  const { stats: net, measure } = useNetworkStats(mapRef);

  const tileX = Math.floor(((viewport.center[0] + 180) / 360) * Math.pow(2, Math.floor(viewport.zoom)));
  const latRad = (viewport.center[1] * Math.PI) / 180;
  const tileY = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
      Math.pow(2, Math.floor(viewport.zoom)),
  );
  const tileZ = Math.floor(viewport.zoom);

  return (
    <div className="absolute bottom-3 left-3 z-20 w-56 rounded-radius-md border border-border-subtle bg-surface-overlay/95 shadow-lg backdrop-blur-sm">
      {/* Viewport */}
      <Section title="Viewport">
        <Row label="Zoom" value={viewport.zoom.toFixed(2)} />
        <Row label="Center" value={`${viewport.center[0].toFixed(4)}, ${viewport.center[1].toFixed(4)}`} />
        <Row label="Pitch / Bearing" value={`${viewport.pitch.toFixed(0)}° / ${viewport.bearing.toFixed(0)}°`} />
        <Row label="Tile" value={`${tileZ}/${tileX}/${tileY}`} mono />
      </Section>

      {/* Layers */}
      <Section title="Layers">
        <Row label="Total" value={String(stats.totalLayers)} />
        <Row label="Visible at zoom" value={`${stats.visibleLayers} / ${stats.totalLayers}`} accent={stats.visibleLayers < stats.totalLayers} />
        <Row label="Hidden (toggle)" value={String(stats.hiddenByVisibility)} warn={stats.hiddenByVisibility > 0} />
        <Row label="Out of zoom range" value={String(stats.outOfZoomRange)} warn={stats.outOfZoomRange > 0} />
        {stats.typeBreakdown.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
            {stats.typeBreakdown.map(({ type, count }) => (
              <span key={type} className="text-[9px] text-text-muted">
                <span className="text-text-secondary">{count}</span> {type}
              </span>
            ))}
          </div>
        )}
      </Section>

      {/* Sources */}
      <Section title="Sources">
        <Row label="Total" value={String(stats.totalSources)} />
        {stats.sourceTypes.length > 0 && (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            {stats.sourceTypes.map(({ type, count }) => (
              <span key={type} className="text-[9px] text-text-muted">
                <span className="text-text-secondary">{count}</span> {type}
              </span>
            ))}
          </div>
        )}
        <Row label="Est. tile requests" value={`~${stats.estimatedTileRequests}`} />
      </Section>

      {/* Network */}
      <Section title="Network" defaultOpen={false}>
        <button
          type="button"
          onClick={measure}
          disabled={net.measuring}
          className={`mb-1.5 w-full rounded-radius-sm px-2 py-1 text-[10px] font-medium transition-colors ${
            net.measuring
              ? "bg-accent text-accent-text"
              : "border border-accent bg-accent/10 text-accent hover:bg-accent hover:text-accent-text"
          }`}
        >
          {net.measuring ? "Reloading & measuring..." : "Measure now"}
        </button>
        {net.totalLoadTime > 0 ? (
          <>
            <Row label="Your connection" value={formatSec(net.totalLoadTime)} warn={net.totalLoadTime > 5000} />
            <Row label="Tiles fetched" value={String(net.tilesLoaded)} />
            <div className="mt-1.5 mb-0.5 text-[9px] font-medium text-text-muted">Estimated load by connection</div>
            <ConnectionTier label="🐌 3G / slow mobile" time={net.totalLoadTime} multiplier={estimateMultiplier(net.totalLoadTime, net.tilesLoaded, "slow")} />
            <ConnectionTier label="📱 4G / fast mobile" time={net.totalLoadTime} multiplier={estimateMultiplier(net.totalLoadTime, net.tilesLoaded, "fast")} />
            <ConnectionTier label="🖥 Desktop / fiber" time={net.totalLoadTime} multiplier={estimateMultiplier(net.totalLoadTime, net.tilesLoaded, "wire")} />
          </>
        ) : (
          <div className="text-[9px] text-text-muted">
            {net.measuring ? "Reloading map & measuring..." : "Reloads the map and measures load time for this viewport"}
          </div>
        )}
      </Section>

      {/* Complexity */}
      <Section title="Complexity" last>
        <Row label="Style size" value={stats.styleSizeKb} />
        <Row label="Expressions" value={`${stats.expressionCount} props`} accent={stats.expressionCount > 50} />
        <Row label="Filters" value={`${stats.filterCount} layers`} />
        {stats.warnings.length > 0 && (
          <div className="mt-1.5 space-y-0.5">
            {stats.warnings.map((w) => (
              <div key={w} className="flex items-start gap-1 text-[9px] text-warning">
                <span className="mt-px">⚠</span>
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

/**
 * Estimates a multiplier to scale the measured load time to a target connection tier.
 *
 * Model: total_time ≈ (tiles × per_tile_latency) + (total_bytes / bandwidth)
 * We approximate by deriving the user's effective throughput from their measurement,
 * then scaling to target tier characteristics.
 *
 * Tier profiles (typical):
 *   slow:  3G —  300ms RTT, ~0.4 Mbps effective
 *   fast:  4G —   80ms RTT, ~5 Mbps effective
 *   wire:  Fiber— 15ms RTT, ~50 Mbps effective
 */
function estimateMultiplier(
  measuredMs: number,
  tiles: number,
  tier: "slow" | "fast" | "wire",
): number {
  if (measuredMs <= 0 || tiles <= 0) return 1;

  // Detect user's approximate tier from their measurement
  const perTile = measuredMs / tiles;

  const profiles = {
    slow: { latency: 300, bwFactor: 0.08 },  // 3G
    fast: { latency: 80, bwFactor: 1 },       // 4G (baseline)
    wire: { latency: 15, bwFactor: 10 },       // Fiber
  };

  const target = profiles[tier];

  // Estimate: scale based on latency ratio (dominant factor for many small tile requests)
  // perTile captures both latency + transfer time for user's connection
  const userLatencyEstimate = Math.max(perTile, 20); // floor at 20ms
  const targetPerTile = target.latency + (perTile - 30) / target.bwFactor; // subtract ~baseline latency, scale transfer
  const clampedTarget = Math.max(targetPerTile, target.latency);

  return clampedTarget / userLatencyEstimate;
}

function ConnectionTier({ label, time, multiplier }: { label: string; time: number; multiplier: number }) {
  const estimated = Math.round(time * multiplier);
  const rating = estimated < 2000 ? "text-success" : estimated < 5000 ? "text-warning" : "text-error";

  return (
    <div className="flex items-center justify-between py-px">
      <span className="text-[9px] text-text-muted">{label}</span>
      <span className={`text-[10px] font-medium ${rating}`}>
        {formatSec(estimated)}
      </span>
    </div>
  );
}

function Section({ title, last, defaultOpen = true, children }: { title: string; last?: boolean; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`${last ? "" : "border-b border-border-subtle"}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1 px-3 py-1.5 text-left hover:bg-surface-sunken/50 transition-colors"
      >
        <span className={`text-[8px] text-text-muted transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted">{title}</span>
      </button>
      {open && <div className="px-3 pb-2">{children}</div>}
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  warn,
  accent,
}: {
  label: string;
  value: string;
  mono?: boolean;
  warn?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-px">
      <span className="text-[10px] text-text-muted">{label}</span>
      <span
        className={`text-[10px] font-medium ${
          warn ? "text-warning" : accent ? "text-accent" : "text-text-secondary"
        } ${mono ? "font-mono" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function formatSec(ms: number): string {
  if (ms < 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── Stats computation ──

interface Stats {
  totalLayers: number;
  visibleLayers: number;
  hiddenByVisibility: number;
  outOfZoomRange: number;
  typeBreakdown: { type: string; count: number }[];
  totalSources: number;
  sourceTypes: { type: string; count: number }[];
  estimatedTileRequests: number;
  styleSizeKb: string;
  expressionCount: number;
  filterCount: number;
  warnings: string[];
}

function computeStats(
  layers: StoreLayer[],
  sources: Record<string, Record<string, unknown>>,
  currentZoom: number,
): Stats {
  let hiddenByVisibility = 0;
  let outOfZoomRange = 0;
  let visibleLayers = 0;
  let expressionCount = 0;
  let filterCount = 0;
  const typeCounts: Record<string, number> = {};
  const sourceIds = new Set<string>();
  const referencedSources = new Set<string>();
  const warnings: string[] = [];

  for (const layer of layers) {
    // Type breakdown
    typeCounts[layer.type] = (typeCounts[layer.type] ?? 0) + 1;

    // Visibility
    const isToggledOff = layer.layout?.visibility === "none";
    const minZ = layer.minzoom ?? 0;
    const maxZ = layer.maxzoom ?? 24;
    const inZoomRange = currentZoom >= minZ && currentZoom < maxZ;

    if (isToggledOff) {
      hiddenByVisibility++;
    } else if (!inZoomRange) {
      outOfZoomRange++;
    } else {
      visibleLayers++;
    }

    // Expressions — count properties that are arrays (expression) vs literals
    if (layer.paint) {
      for (const val of Object.values(layer.paint)) {
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === "string") {
          expressionCount++;
        }
      }
    }
    if (layer.layout) {
      for (const [key, val] of Object.entries(layer.layout)) {
        if (key === "visibility") continue;
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === "string") {
          expressionCount++;
        }
      }
    }

    // Filters
    if (layer.filter) filterCount++;

    // Track source references
    if (layer.source) referencedSources.add(layer.source);
  }

  // Sources
  for (const id of Object.keys(sources)) {
    sourceIds.add(id);
  }
  const sourceTypeCounts: Record<string, number> = {};
  let tileSources = 0;
  for (const src of Object.values(sources)) {
    const t = (src.type as string) ?? "unknown";
    sourceTypeCounts[t] = (sourceTypeCounts[t] ?? 0) + 1;
    if (t === "vector" || t === "raster" || t === "raster-dem") {
      tileSources++;
    }
  }

  // Estimated tile requests: ~9 tiles visible on screen per source at typical viewport
  const estimatedTileRequests = tileSources * 9;

  // Style size
  let styleSizeKb: string;
  try {
    const bytes = new Blob([JSON.stringify({ ...({} as Record<string, unknown>), layers, sources })]).size;
    styleSizeKb = bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
  } catch {
    styleSizeKb = "—";
  }

  // Warnings
  for (const srcId of referencedSources) {
    if (!sourceIds.has(srcId)) {
      warnings.push(`Orphan reference: source "${srcId}" not found`);
    }
  }

  const idSet = new Set<string>();
  for (const layer of layers) {
    if (idSet.has(layer.id)) {
      warnings.push(`Duplicate layer ID: "${layer.id}"`);
    }
    idSet.add(layer.id);
  }

  if (layers.length > 200) {
    warnings.push(`${layers.length} layers — may impact rendering performance`);
  }

  return {
    totalLayers: layers.length,
    visibleLayers,
    hiddenByVisibility,
    outOfZoomRange,
    typeBreakdown: Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ type, count })),
    totalSources: sourceIds.size,
    sourceTypes: Object.entries(sourceTypeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ type, count })),
    estimatedTileRequests,
    styleSizeKb,
    expressionCount,
    filterCount,
    warnings,
  };
}
